// Glyphes du DS v2 : nom du kit → glyphe Lucide, table reprise de `core/iconMap.js` (amont).
// Glyphes © Lucide Contributors, licence ISC (leur en-tête d'attribution est retiré des masques).
//
// SVG importés du paquet et non du CDN unpkg de l'amont : la CSP est en `default-src 'self'`.
// Hors de `components/`, dont tout module est auto-enregistré comme composant par Nuxt.
import arrowRightLeft from 'lucide-static/icons/arrow-right-left.svg?raw';
import arrowUpDown from 'lucide-static/icons/arrow-up-down.svg?raw';
import banknoteArrowDown from 'lucide-static/icons/banknote-arrow-down.svg?raw';
import banknoteArrowUp from 'lucide-static/icons/banknote-arrow-up.svg?raw';
import bell from 'lucide-static/icons/bell.svg?raw';
import briefcase from 'lucide-static/icons/briefcase.svg?raw';
import building2 from 'lucide-static/icons/building-2.svg?raw';
import calculator from 'lucide-static/icons/calculator.svg?raw';
import calendar from 'lucide-static/icons/calendar.svg?raw';
import chartNoAxesColumn from 'lucide-static/icons/chart-no-axes-column.svg?raw';
import check from 'lucide-static/icons/check.svg?raw';
import chevronDown from 'lucide-static/icons/chevron-down.svg?raw';
import chevronLeft from 'lucide-static/icons/chevron-left.svg?raw';
import chevronRight from 'lucide-static/icons/chevron-right.svg?raw';
import chevronUp from 'lucide-static/icons/chevron-up.svg?raw';
import circleHelp from 'lucide-static/icons/circle-help.svg?raw';
import circlePlus from 'lucide-static/icons/circle-plus.svg?raw';
import circleX from 'lucide-static/icons/circle-x.svg?raw';
import clock from 'lucide-static/icons/clock.svg?raw';
import copy from 'lucide-static/icons/copy.svg?raw';
import creditCard from 'lucide-static/icons/credit-card.svg?raw';
import crosshair from 'lucide-static/icons/crosshair.svg?raw';
import externalLink from 'lucide-static/icons/external-link.svg?raw';
import file from 'lucide-static/icons/file.svg?raw';
import filePenLine from 'lucide-static/icons/file-pen-line.svg?raw';
import fileStack from 'lucide-static/icons/file-stack.svg?raw';
import filter from 'lucide-static/icons/filter.svg?raw';
import folder from 'lucide-static/icons/folder.svg?raw';
import folderOpen from 'lucide-static/icons/folder-open.svg?raw';
import gauge from 'lucide-static/icons/gauge.svg?raw';
import history from 'lucide-static/icons/history.svg?raw';
import house from 'lucide-static/icons/house.svg?raw';
import circleAlert from 'lucide-static/icons/circle-alert.svg?raw';
import info from 'lucide-static/icons/info.svg?raw';
import layoutDashboard from 'lucide-static/icons/layout-dashboard.svg?raw';
import link from 'lucide-static/icons/link.svg?raw';
import list from 'lucide-static/icons/list.svg?raw';
import lock from 'lucide-static/icons/lock.svg?raw';
import lockOpen from 'lucide-static/icons/lock-open.svg?raw';
import logOut from 'lucide-static/icons/log-out.svg?raw';
import mail from 'lucide-static/icons/mail.svg?raw';
import menu from 'lucide-static/icons/menu.svg?raw';
import messageSquare from 'lucide-static/icons/message-square.svg?raw';
import messageSquareText from 'lucide-static/icons/message-square-text.svg?raw';
import messagesSquare from 'lucide-static/icons/messages-square.svg?raw';
import minus from 'lucide-static/icons/minus.svg?raw';
import moon from 'lucide-static/icons/moon.svg?raw';
import pencil from 'lucide-static/icons/pencil.svg?raw';
import phone from 'lucide-static/icons/phone.svg?raw';
import play from 'lucide-static/icons/play.svg?raw';
import plus from 'lucide-static/icons/plus.svg?raw';
import receiptText from 'lucide-static/icons/receipt-text.svg?raw';
import refreshCw from 'lucide-static/icons/refresh-cw.svg?raw';
import save from 'lucide-static/icons/save.svg?raw';
import scale from 'lucide-static/icons/scale.svg?raw';
import smartphone from 'lucide-static/icons/smartphone.svg?raw';
import search from 'lucide-static/icons/search.svg?raw';
import send from 'lucide-static/icons/send.svg?raw';
import settings from 'lucide-static/icons/settings.svg?raw';
import sun from 'lucide-static/icons/sun.svg?raw';
import tag from 'lucide-static/icons/tag.svg?raw';
import target from 'lucide-static/icons/target.svg?raw';
import trash2 from 'lucide-static/icons/trash-2.svg?raw';
import triangleAlert from 'lucide-static/icons/triangle-alert.svg?raw';
import user from 'lucide-static/icons/user.svg?raw';
import wallet from 'lucide-static/icons/wallet.svg?raw';
import x from 'lucide-static/icons/x.svg?raw';

// Retire ce que le masque n'utilise pas, avant l'encodage où chaque espace vaudrait `%20`.
// `width`/`height` ne sont inertes que sur la racine (`mask-size: contain`) : les formes internes en
// ont aussi, et un `<rect>` privé des siennes ne se dessine plus.
const clean = (svg: string): string => svg
  .replace(/<!--[\s\S]*?-->/g, '')
  .replace(/<svg\b[^>]*>/, tag => tag.replace(/\s(?:class|width|height)="[^"]*"/g, ''))
  .replace(/\s+/g, ' ')
  .trim();

// `angle-up`, `save` et `times-circle` sont absents de la table amont : le kit ne les utilise pas.
const RAW = {
  'angle-down': chevronDown,
  'angle-left': chevronLeft,
  'angle-right': chevronRight,
  'angle-up': chevronUp,
  'arrow-right-left': arrowRightLeft,
  'banknote-arrow-down': banknoteArrowDown,
  'banknote-arrow-up': banknoteArrowUp,
  'bars': menu,
  bell,
  briefcase,
  'building': building2,
  calculator,
  calendar,
  'chart-bar': chartNoAxesColumn,
  check,
  'cog': settings,
  'comment': messageSquare,
  // Ligne commentée : le kit pose `message-square-text` (bulle avec des lignes de texte), pas une
  // bulle pleine : le glyphe vient du pack, la version remplie est dérivée ici.
  'comment-text': messageSquareText,
  copy,
  'credit-card': creditCard,
  crosshair,
  'dashboard': layoutDashboard,
  'envelope': mail,
  'exclamation-triangle': triangleAlert,
  'external-link': externalLink,
  file,
  'file-edit': filePenLine,
  'file-stack': fileStack,
  filter,
  folder,
  'folder-docs': folderOpen,
  gauge,
  history,
  'home': house,
  'circle-alert': circleAlert,
  clock,
  'info-circle': info,
  link,
  list,
  lock,
  'lock-open': lockOpen,
  'messages': messagesSquare,
  'mobile': smartphone,
  minus,
  moon,
  pencil,
  phone,
  play,
  plus,
  'plus-circle': circlePlus,
  'question-circle': circleHelp,
  'receipt-text': receiptText,
  'refresh': refreshCw,
  save,
  scale,
  search,
  send,
  'sign-out': logOut,
  'sort-alt': arrowUpDown,
  sun,
  tag,
  target,
  'times': x,
  'times-circle': circleX,
  'trash': trash2,
  user,
  wallet,
} as const;

// Union des noms servie à `DsIcon` et aux tables de navigation, pour que le compilateur refuse un
// nom absent du set.
export type DsIconName = keyof typeof RAW;

export const DS_ICON_SOURCES = Object.fromEntries(
  Object.entries(RAW).map(([name, svg]) => [name, clean(svg)]),
) as Record<DsIconName, string>;
